import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export interface Organization {
    id: string;
    name: string;
    slug: string;
    logo_url: string | null;
    brand_color: string | null;
    locale: string | null;
    currency: string | null;
    industry: string | null;
    industry_custom: string | null;
    created_at: string;
    updated_at: string;
}

export type OrgRole = 'owner' | 'admin' | 'member';

const ACTIVE_ORG_KEY = 'quotio.activeOrgId';

interface UseCurrentOrgResult {
    org: Organization | null;
    role: OrgRole | null;
    orgs: Organization[];
    rolesByOrg: Record<string, OrgRole>;
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
    switchOrg: (orgId: string) => void;
    createOrg: (payload: {
        name: string;
        industry?: string;
        industryCustom?: string;
    }) => Promise<Organization>;
}

function readActiveOrgId(): string | null {
    if (typeof window === 'undefined') return null;
    try {
        return window.localStorage.getItem(ACTIVE_ORG_KEY);
    } catch {
        return null;
    }
}

function writeActiveOrgId(id: string | null) {
    if (typeof window === 'undefined') return;
    try {
        if (id) window.localStorage.setItem(ACTIVE_ORG_KEY, id);
        else window.localStorage.removeItem(ACTIVE_ORG_KEY);
    } catch {
        /* ignore */
    }
}

/**
 * Récupère l'ensemble des organisations de l'user (via organization_members),
 * expose l'org active (persistée en localStorage) + fonctions pour switcher /
 * créer une nouvelle org.
 */
export function useCurrentOrg(): UseCurrentOrgResult {
    const { user, loading: authLoading } = useAuth();
    const [orgs, setOrgs] = useState<Organization[]>([]);
    const [rolesByOrg, setRolesByOrg] = useState<Record<string, OrgRole>>({});
    const [activeOrgId, setActiveOrgId] = useState<string | null>(() => readActiveOrgId());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchOrgs = useCallback(async () => {
        if (!user) {
            setOrgs([]);
            setRolesByOrg({});
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        const { data, error: err } = await supabase
            .from('organization_members')
            .select('role, organizations:organization_id(*)')
            .eq('user_id', user.id);

        if (err) {
            setError(err.message);
            setLoading(false);
            return;
        }

        const list: Organization[] = [];
        const roles: Record<string, OrgRole> = {};
        for (const row of data ?? []) {
            const rawOrg = row.organizations as unknown as Organization | Organization[] | null;
            const resolved = Array.isArray(rawOrg) ? rawOrg[0] ?? null : rawOrg;
            if (!resolved) continue;
            list.push(resolved);
            roles[resolved.id] = row.role as OrgRole;
        }

        // Tri : plus récente d'abord (created_at)
        list.sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );

        setOrgs(list);
        setRolesByOrg(roles);

        // Si l'org active stockée n'existe plus dans la liste, on bascule sur la 1ère.
        setActiveOrgId((prev) => {
            if (prev && list.some((o) => o.id === prev)) return prev;
            const fallback = list[0]?.id ?? null;
            writeActiveOrgId(fallback);
            return fallback;
        });

        setLoading(false);
    }, [user]);

    useEffect(() => {
        if (authLoading) return;
        void fetchOrgs();
    }, [authLoading, fetchOrgs]);

    const switchOrg = useCallback((orgId: string) => {
        setActiveOrgId(orgId);
        writeActiveOrgId(orgId);
    }, []);

    const createOrg = useCallback(
        async ({
            name,
            industry,
            industryCustom,
        }: {
            name: string;
            industry?: string;
            industryCustom?: string;
        }): Promise<Organization> => {
            if (!user) throw new Error('Non authentifié');

            const { data, error: err } = await supabase.rpc('create_organization', {
                org_name: name,
                org_industry: industry ?? null,
                org_industry_custom: industryCustom?.trim() ? industryCustom.trim() : null,
            });

            if (err) throw err;

            const created = data as Organization;

            // On bascule automatiquement sur la nouvelle org créée
            if (created?.id) {
                setActiveOrgId(created.id);
                writeActiveOrgId(created.id);
            }

            await fetchOrgs();
            return created;
        },
        [user, fetchOrgs]
    );

    const org = orgs.find((o) => o.id === activeOrgId) ?? null;
    const role = org ? rolesByOrg[org.id] ?? null : null;

    return {
        org,
        role,
        orgs,
        rolesByOrg,
        loading: authLoading || loading,
        error,
        refetch: fetchOrgs,
        switchOrg,
        createOrg,
    };
}
